import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { AddressAutocompleteProps, AddressDetails, PlaceType } from './types';

let cachedAutocompleteService: google.maps.places.AutocompleteService | null = null;
let cachedGeocoder: google.maps.Geocoder | null = null;

const getAutocompleteService = (): google.maps.places.AutocompleteService | null => {
  if (!cachedAutocompleteService && typeof window !== 'undefined' && window.google?.maps?.places) {
    cachedAutocompleteService = new window.google.maps.places.AutocompleteService();
  }
  return cachedAutocompleteService;
};

const getGeocoder = (): google.maps.Geocoder | null => {
  if (!cachedGeocoder && typeof window !== 'undefined' && window.google?.maps) {
    cachedGeocoder = new window.google.maps.Geocoder();
  }
  return cachedGeocoder;
};

const toCountryIso = (country: string): string => {
  const clean = country.trim().toLowerCase();
  const countryMap: Record<string, string> = {
    mex: 'mx',
    chl: 'cl',
    col: 'co',
    per: 'pe',
    mx: 'mx',
    cl: 'cl',
    co: 'co',
    pe: 'pe',
  };
  return countryMap[clean] ?? clean;
};

const findComponent = (
  components: google.maps.GeocoderAddressComponent[] | undefined,
  type: string,
  useLongName = false,
): string => {
  const item = components?.find((c) => c.types.includes(type));
  if (!item) return '';
  return useLongName ? item.long_name : item.short_name;
};

const DEFAULT_PLACE_TYPES: string[] = ['address'];

export const useAddressAutocomplete = (props: AddressAutocompleteProps) => {
  const { value, country, minLength = 4, debounceMs = 350, throttleMs, types } = props;
  const effectiveDelay = debounceMs ?? throttleMs ?? 350;

  const countryCode = useMemo(() => toCountryIso(country), [country]);
  const placeTypes = types ?? DEFAULT_PLACE_TYPES;

  const countryCodeRef = useRef(countryCode);
  countryCodeRef.current = countryCode;

  const placeTypesRef = useRef(placeTypes);
  placeTypesRef.current = placeTypes;

  const [inputValue, setInputValue] = useState(value || '');
  const [prevValue, setPrevValue] = useState(value);
  const [rawOptions, setRawOptions] = useState<readonly PlaceType[]>([]);
  const [rawLoading, setRawLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Sync external value updates during render without triggering effect cascades
  if (value !== prevValue) {
    setPrevValue(value);
    setInputValue(value || '');
    setRawOptions([]);
    setRawLoading(false);
  }

  // Ref tracks active fetch to discard stale async responses or responses after unmount
  const requestIdRef = useRef(0);

  const fetchSuggestions = useMemo(
    () =>
      debounce((input: string, requestId: number) => {
        const service = getAutocompleteService();
        if (!service) {
          setRawLoading(false);
          return;
        }

        service.getPlacePredictions(
          {
            input,
            componentRestrictions: { country: countryCodeRef.current },
            types: placeTypesRef.current,
          },
          (predictions) => {
            if (requestId !== requestIdRef.current) return;

            setRawLoading(false);
            if (predictions && predictions.length > 0) {
              const newOptions: PlaceType[] = predictions.map((prediction) => ({
                description: prediction.description,
                place_id: prediction.place_id,
              }));
              setRawOptions(newOptions);
            } else {
              setRawOptions([]);
            }
          },
        );
      }, effectiveDelay),
    [effectiveDelay],
  );

  // Clean up pending debounce and invalidate in-flight callbacks on unmount
  useEffect(
    () => () => {
      fetchSuggestions.cancel();
      requestIdRef.current += 1;
    },
    [fetchSuggestions],
  );

  const onSelectOption = (selected: PlaceType | null) => {
    fetchSuggestions.cancel();
    requestIdRef.current += 1;

    if (!selected) {
      setInputValue('');
      setRawOptions([]);
      setRawLoading(false);
      setOpen(false);
      props.onChange?.('');
      return;
    }

    setInputValue(selected.description);
    setRawOptions([]);
    setRawLoading(false);
    setOpen(false);
    props.onChange?.(selected.description);

    const geocoderInstance = getGeocoder();
    if (!geocoderInstance) return;

    geocoderInstance.geocode(
      {
        placeId: selected.place_id,
      },
      (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const result = results[0];
          const components = result.address_components;
          const geometry = result.geometry.location;

          const zipCode = findComponent(components, 'postal_code', true);
          const state = findComponent(components, 'administrative_area_level_1', true);
          const city =
            findComponent(components, 'locality', true) ||
            findComponent(components, 'administrative_area_level_2', true) ||
            findComponent(components, 'administrative_area_level_3', true) ||
            state;
          const neighborhood =
            findComponent(components, 'sublocality_level_1', true) ||
            findComponent(components, 'neighborhood', true) ||
            findComponent(components, 'sublocality', true);
          const streetNumber = findComponent(components, 'street_number', true);
          const resolvedCountry = findComponent(components, 'country');

          const details: AddressDetails = {
            address: selected.description,
            label: selected.description,
            placeId: selected.place_id,
            isFixture: false,
            latitude: geometry.lat(),
            longitude: geometry.lng(),
            location: {
              lat: geometry.lat(),
              lng: geometry.lng(),
            },
            zipCode,
            city,
            state,
            country: resolvedCountry,
            streetNumber,
            neighborhood,
            gmaps: result,
          };

          props.onSelectAddress?.(details);
        }
      },
    );
  };

  const onInputChange = (newInputValue: string, reason: string) => {
    if (reason === 'input') {
      setInputValue(newInputValue);
      props.onChange?.(newInputValue);

      if (newInputValue.length < minLength) {
        fetchSuggestions.cancel();
        requestIdRef.current += 1;
        setRawLoading(false);
        setRawOptions([]);
        return;
      }

      setRawLoading(true);
      const nextRequestId = requestIdRef.current + 1;
      requestIdRef.current = nextRequestId;
      fetchSuggestions(newInputValue, nextRequestId);
    } else if (reason === 'clear') {
      fetchSuggestions.cancel();
      requestIdRef.current += 1;
      setInputValue('');
      setRawLoading(false);
      setRawOptions([]);
      props.onChange?.('');
    }
  };

  // State derivation: guarantees invariant without triggering extra renders
  const isBelowMin = inputValue.length < minLength;
  const options = isBelowMin ? [] : rawOptions;
  const loading = isBelowMin ? false : rawLoading;

  return {
    inputValue,
    options,
    loading,
    open,
    setOpen,
    onSelectOption,
    onInputChange,
  };
};
